import { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import {
  Search,
  Download,
  Eye,
  Calendar,
  User,
  FileText,
  Package,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  ChevronRight,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';
import { API_URL } from '../../Config';

// Composant Toast (à importer ou définir ici)
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-emerald-500' : 
                  type === 'error' ? 'bg-rose-500' : 
                  'bg-blue-500';

  return (
    <div className={`fixed top-6 right-6 z-50 px-4 py-3 ${bgColor} text-white rounded-lg shadow-lg transform transition-transform duration-300 animate-slide-in`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {type === 'success' && (
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {type === 'error' && (
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className="font-medium">{message}</span>
        </div>
        <button 
          onClick={onClose}
          className="ml-4 text-white hover:text-gray-200 focus:outline-none"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default function TousHistoriques() {
  const [demandes, setDemandes] = useState([]);
  const [historiques, setHistoriques] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [expandedRow, setExpandedRow] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ ...toast, show: false });
  };

  const loadDemandes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/demandes`, { headers });
      const demandesData = res.data.data?.data || res.data.data || [];
      setDemandes(demandesData);

      // Charger les historiques uniquement pour les premières demandes
      const demandesToLoad = demandesData.slice(0, 50);
      const histPromises = demandesToLoad.map(d =>
        axios.get(`${API_URL}/historique/demande/${d.id}`, { headers })
          .then(r => ({ id: d.id, data: r.data.data?.historique?.data || [] }))
          .catch(() => ({ id: d.id, data: [] }))
      );

      const results = await Promise.allSettled(histPromises);
      const histMap = {};
      results.forEach(result => {
        if (result.status === 'fulfilled') {
          histMap[result.value.id] = result.value.data;
        }
      });
      setHistoriques(histMap);

    } catch (err) {
      console.error('Erreur de chargement:', err);
      showToast('Erreur lors du chargement des historiques', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDemandes();
  }, []);

  const getStatusInfo = (status) => {
    const statusMap = {
      en_attente: {
        label: 'En attente',
        color: 'bg-amber-100 text-amber-800',
        icon: Clock,
        dotColor: 'bg-amber-500'
      },
      validee: {
        label: 'Validée',
        color: 'bg-emerald-100 text-emerald-800',
        icon: CheckCircle,
        dotColor: 'bg-emerald-500'
      },
      refusee: {
        label: 'Refusée',
        color: 'bg-rose-100 text-rose-800',
        icon: XCircle,
        dotColor: 'bg-rose-500'
      },
      annulee: {
        label: 'Annulée',
        color: 'bg-gray-100 text-gray-800',
        icon: AlertCircle,
        dotColor: '0'
      },
      pret_recuperation: {
        label: 'Prêt à récupérer',
        color: 'bg-blue-100 text-blue-800',
        icon: Package,
        dotColor: 'bg-blue-500'
      },
      en_preparation: {
        label: 'En préparation',
        color: 'bg-indigo-100 text-indigo-800',
        icon: Package,
        dotColor: 'bg-indigo-500'
      },
    };
    return statusMap[status] || {
      label: status || 'Inconnu',
      color: 'bg-gray-100 text-gray-800',
      icon: AlertCircle,
      dotColor: 'bg-gray-400'
    };
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch {
      return 'Date invalide';
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedDemandes = [...demandes].sort((a, b) => {
    if (sortConfig.key === 'created_at') {
      return sortConfig.direction === 'asc' 
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at);
    }
    if (sortConfig.key === 'status') {
      return sortConfig.direction === 'asc'
        ? getStatusInfo(a.status).label.localeCompare(getStatusInfo(b.status).label)
        : getStatusInfo(b.status).label.localeCompare(getStatusInfo(a.status).label);
    }
    return 0;
  });

  const filteredDemandes = sortedDemandes.filter(demande => {
    const matchesSearch = 
      !search ||
      (demande.numero && demande.numero.toLowerCase().includes(search.toLowerCase())) ||
      (demande.demandeur?.nom && demande.demandeur.nom.toLowerCase().includes(search.toLowerCase())) ||
      demande.id.toString().includes(search);
    
    const matchesStatus = 
      !filterStatus || 
      demande.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const countEvents = (demandeId) => {
    return historiques[demandeId]?.length || 0;
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const getEventPreview = (demandeId) => {
    const events = historiques[demandeId] || [];
    if (events.length === 0) return 'Aucun événement';
    
    const lastEvent = events[events.length - 1];
    return `${lastEvent.type_action || 'Événement'} • ${formatDisplayDate(lastEvent.created_at)}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Toast Notification */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={hideToast}
        />
      )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Historique des Demandes</h1>
              <p className="text-gray-500 mt-1">Suivi complet de toutes les demandes de matériel</p>
            </div>
            <button
              onClick={loadDemandes}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover: transition-colors flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Actualiser
            </button>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-gray-900">{demandes.length}</div>
              <div className="text-sm text-gray-500">Total demandes</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-amber-600">
                {demandes.filter(d => d.status === 'en_attente').length}
              </div>
              <div className="text-sm text-gray-500">En attente</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-emerald-600">
                {demandes.filter(d => d.status === 'validee').length}
              </div>
              <div className="text-sm text-gray-500">Validées</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-blue-600">
                {Object.values(historiques).reduce((sum, events) => sum + (events?.length || 0), 0)}
              </div>
              <div className="text-sm text-gray-500">Événements</div>
            </div>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="mb-6 bg-white p-4 rounded-lg border">
          <div className="text-sm font-medium text-gray-700 mb-3">Recherche et filtres</div>
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par ID, numéro ou demandeur..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="w-full lg:w-64">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Tous les statuts</option>
                  <option value="en_attente">En attente</option>
                  <option value="validee">Validée</option>
                  <option value="refusee">Refusée</option>
                  <option value="annulee">Annulée</option>
                  <option value="pret_recuperation">Prêt à récupérer</option>
                  <option value="en_preparation">En préparation</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b">
                <tr>
                 <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                    N° Demande
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer hover:"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      Date
                      {sortConfig.key === 'created_at' && (
                        sortConfig.direction === 'asc' ? 
                          <ChevronUp className="h-4 w-4" /> : 
                          <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </th>               
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                    Demandeur
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer hover:"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      Statut
                      {sortConfig.key === 'status' && (
                        sortConfig.direction === 'asc' ? 
                          <ChevronUp className="h-4 w-4" /> : 
                          <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                   Matériels
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredDemandes.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="text-gray-400 mb-3">
                        <FileText className="w-12 h-12 mx-auto" />
                      </div>
                      <p className="text-gray-500 mb-4">Aucune demande trouvée</p>
                      {(search || filterStatus) && (
                        <p className="text-sm text-gray-400 mt-2">
                          Essayez de modifier vos critères de recherche
                        </p>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredDemandes.map((demande, index) => {
                    const statusInfo = getStatusInfo(demande.status);
                    const StatusIcon = statusInfo.icon;
                    const isExpanded = expandedRow === demande.id;
                    
                    return (
                      <>
                        <tr 
                          key={demande.id} 
                          className={`hover: cursor-pointer ${isExpanded ? 'bg-blue-50' : ''}`}
                          onClick={() => toggleRow(demande.id)}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <FileText className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-900">
                                #{demande.numero || demande.id}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                              <div className="text-sm text-gray-900">
                                {formatDisplayDate(demande.created_at)}
                              </div>
                            </div>
                          </td>
                          
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <User className="h-4 w-4 text-gray-400 mr-2" />
                              <div>
                                <div className="text-sm text-gray-900">
                                  {demande.demandeur?.nom || 'N/A'}
                                </div>
                               
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${statusInfo.dotColor}`} />
                              <span className={`text-xs px-3 py-1 rounded-full ${statusInfo.color}`}>
                                {statusInfo.label}
                              </span>
                            </div>
                          </td>
                        
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <Package className="h-4 w-4 text-gray-400 mr-2" />
                              <span className={`text-sm font-medium ${
                                countEvents(demande.id) > 0 ? 'text-gray-900' : 'text-gray-400'
                              }`}>
                                {countEvents(demande.id)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/demandes/historique/${demande.id}`}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 hover:bg-blue-50 rounded"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Eye className="h-4 w-4 inline mr-1" />
                              </Link>
                              <button
                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRow(demande.id);
                                }}
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Row détaillée */}
                        {isExpanded && (
                          <tr className="bg-blue-50">
                            <td colSpan="8" className="px-6 py-4">
                              <div className="pl-4 border-l-4 border-blue-200">
                                <div className="flex justify-between items-center mb-3">
                                  <h4 className="text-sm font-semibold text-gray-900">
                                    Événements récents
                                  </h4>
                                  <Link
                                    to={`/demandes/historique/${demande.id}`}
                                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                  >
                                    Voir tout
                                    <ExternalLink className="h-3 w-3" />
                                  </Link>
                                </div>
                                
                                {historiques[demande.id]?.length > 0 ? (
                                  <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {historiques[demande.id].slice(-5).reverse().map((event, idx) => (
                                      <div key={idx} className="text-sm bg-white p-3 rounded border">
                                        <div className="flex justify-between items-start mb-1">
                                          <span className="font-medium text-gray-900 capitalize">
                                            {event.type_action?.replace(/_/g, ' ')}
                                          </span>
                                          <span className="text-xs text-gray-500">
                                            {formatDisplayDate(event.created_at)}
                                          </span>
                                        </div>
                                        <div className="text-gray-600">
                                          Par <span className="font-medium">{event.user?.nom || 'Système'}</span>
                                          {event.description && (
                                            <div className="mt-1 text-gray-500">{event.description}</div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-500 italic">
                                    Aucun événement enregistré
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {filteredDemandes.length > 0 && (
            <div className="px-6 py-4 border-t">
              <div className="flex justify-between items-center text-sm text-gray-600">
                <div>
                  Affichage de 1 à {filteredDemandes.length} sur {demandes.length} demandes
                </div>
                <div>
                  Total événements : {Object.values(historiques).reduce((sum, events) => sum + (events?.length || 0), 0)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}